import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateProductRequestSchema, type CreateProductRequest } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface Props {
  isPending: boolean;
  onSubmit: (values: CreateProductRequest) => Promise<void>;
}

/**
 * Spec 081 — o cadastro do produto, extraído de `pages/Products.tsx` pelo teto de 200 linhas: ele
 * ganhou a unidade de CONTAGEM e a EQUIVALÊNCIA, que são o que faz a leitura de um contador virar
 * quantidade na unidade da linha.
 */
export function ProductForm({ isPending, onSubmit }: Props) {
  const form = useForm<CreateProductRequest>({
    resolver: zodResolver(CreateProductRequestSchema),
    // Campo numérico vazio por padrão — nunca um literal a apagar antes de digitar. Valia para o
    // `0` da regra e vale igual para o `1`: quem cadastra uma caixa de 10 kg apaga o 1 antes de
    // escrever 10, que é exatamente o atrito que a regra existe para remover. O `placeholder`
    // carrega o caso comum sem preencher o campo.
    defaultValues: {
      name: "",
      description: "",
      unit: "un",
      countingUnit: "un",
      unitFactor: undefined,
      pricePerUnit: undefined,
    },
  });

  async function submit(values: CreateProductRequest) {
    await onSubmit(values);
    form.reset();
  }

  return (
    <Card className="mb-6 animate-scale-in">
      <CardHeader>
        <CardTitle>Novo produto</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => void form.handleSubmit(submit)(e)}
          className="grid gap-4 md:grid-cols-2"
        >
          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input id="name" {...form.register("name")} />
            {form.formState.errors.name ? (
              <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="unit">Unidade</Label>
            <Input id="unit" {...form.register("unit")} placeholder="un, kg, L..." />
          </div>
          <div className="space-y-2">
            <Label htmlFor="countingUnit">Unidade de contagem</Label>
            <Input
              id="countingUnit"
              {...form.register("countingUnit")}
              placeholder="caixa, pacote, kg..."
            />
            <p className="text-xs text-muted-foreground">
              Como este produto é contado no chão de fábrica. Pode ser diferente da unidade de venda
              acima.
            </p>
            {form.formState.errors.countingUnit ? (
              <p className="text-xs text-destructive">
                {form.formState.errors.countingUnit.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="unitFactor">Equivalência</Label>
            <Input
              id="unitFactor"
              type="number"
              step="any"
              min="0"
              placeholder="1"
              {...form.register("unitFactor", { valueAsNumber: true })}
            />
            <p className="text-xs text-muted-foreground">
              Quanto vale 1 unidade de contagem na unidade de produção da linha. Uma caixa de 10 kg
              numa linha que mede em quilo vale 10.
            </p>
            {form.formState.errors.unitFactor ? (
              <p className="text-xs text-destructive">{form.formState.errors.unitFactor.message}</p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="pricePerUnit">Preço por unidade (R$)</Label>
            <Input
              id="pricePerUnit"
              type="number"
              step="0.01"
              placeholder="0"
              {...form.register("pricePerUnit", { valueAsNumber: true })}
            />
            {form.formState.errors.pricePerUnit ? (
              <p className="text-xs text-destructive">
                {form.formState.errors.pricePerUnit.message}
              </p>
            ) : null}
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="description">Descrição (opcional)</Label>
            <Input id="description" {...form.register("description")} />
          </div>
          <div className="md:col-span-2">
            <Button type="submit" disabled={isPending}>
              {isPending ? "Criando…" : "Criar produto"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
